import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import { LuMailOpen, LuReply, LuSend, LuTrash2 } from 'react-icons/lu';
import api from '../../../services/api';

// ── Quote-tail stripping ──────────────────────────────────────────────────────
//
// Every major email client (Gmail, Outlook, Apple Mail, Superhuman) appends the
// prior thread as a quoted tail below the new reply text. We strip it at
// DISPLAY time only — the full body_text is preserved in MongoDB for auditability,
// re-classification, and future feature use.
//
// Strategy: scan for the earliest occurrence of any known delimiter pattern.
// Patterns are ordered from most-specific to least-specific so a precise match
// is always preferred over a broad catch-all.
//
// Supported delimiters:
//   Gmail (English):  "On Mon, Mar 23, 2026 at 8:04 PM <x@y.com> wrote:"
//   Gmail (re-quoted): "> On Mon, ..."
//   Outlook/Exchange: 16+ underscores on their own line
//   Outlook forward:  "-----Original Message-----"
//   Outlook reply:    "From: Name\nSent: ..." two-line block
//   Plain-text quote: lines starting with "> " or ">>"

const QUOTE_DELIMITERS = [
  // Gmail English attribution line.
  // Requires the strict weekday+month+date+year+time pattern so it never
  // fires on natural prose starting with "On the other hand..." etc.
  /^On [A-Z][a-z]{2}, [A-Z][a-z]{2} \d{1,2}, \d{4} at \d{1,2}:\d{2}/m,

  // Same pattern inside a ">" quote marker (re-quoted in multi-hop threads).
  /^> On [A-Z][a-z]{2}/m,

  // Outlook / Exchange visual separator line (16+ underscores).
  /^_{16,}\s*$/m,

  // "-----Original Message-----" — Outlook, Lotus Notes, many enterprise mailers.
  /^-{5,}\s*Original Message\s*-{5,}/im,

  // Outlook reply header block with newlines (new messages stored with line breaks).
  /^From:\s+.+[\r\n]+Sent:/m,

  // Outlook reply header compressed onto one line — matches messages stored before
  // the _strip_html fix (no newlines). "Sent: Mon|Tue|..." is specific enough to
  // avoid false-positives on natural prose containing "From:".
  /\bFrom:\s+\S.{0,120}?\bSent:\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)/,

  // Catch-all: first line that is a plain-text quote marker.
  // Requires "> " or ">>" to avoid matching a lone ">" in markdown-style prose.
  /^>[ >]/m,
];

/**
 * Return the new-reply portion of an email body by stripping the quoted tail.
 *
 * Scans all delimiter patterns simultaneously and cuts at the earliest match.
 * If the entire body is a quote (stripped result is empty), falls back to the
 * full text so the UI never renders a blank message card.
 *
 * @param {string} text  Raw body_text stored in MongoDB.
 * @returns {string}     New-reply content only, trailing whitespace removed.
 */
const stripQuotedTail = (text) => {
  if (!text) return '';

  let cutAt = text.length;

  for (const pattern of QUOTE_DELIMITERS) {
    const match = text.match(pattern);
    if (match !== null && match.index < cutAt) {
      cutAt = match.index;
    }
  }

  const stripped = text.slice(0, cutAt).trimEnd();
  // Fallback: if the whole body was the quote header (stripped is empty),
  // show the full text rather than a blank card.
  return stripped.length > 0 ? stripped : text.trimEnd();
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const ET = 'America/New_York';

const formatFullTime = (isoString) => {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString('en-US', {
    timeZone: ET,
    weekday:  'short',
    month:    'short',
    day:      'numeric',
    hour:     'numeric',
    minute:   '2-digit',
  }) + ' ET';
};

const initials = (email = '') => {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return local.slice(0, 2).toUpperCase();
};

// ── Avatar ────────────────────────────────────────────────────────────────────

const Avatar = ({ email, isOutbound }) => (
  <Box
    flexShrink={0}
    w="34px"
    h="34px"
    borderRadius="full"
    bg={isOutbound ? 'rgba(157, 113, 253, 0.22)' : 'rgba(99, 179, 237, 0.18)'}
    border="1px solid"
    borderColor={isOutbound ? 'rgba(157, 113, 253, 0.35)' : 'rgba(99, 179, 237, 0.28)'}
    display="flex"
    alignItems="center"
    justifyContent="center"
  >
    <Text
      fontSize="11px"
      fontWeight="600"
      color={isOutbound ? 'rgba(157, 113, 253, 0.9)' : 'rgba(99, 179, 237, 0.9)'}
      letterSpacing="0.03em"
      lineHeight={1}
    >
      {initials(email)}
    </Text>
  </Box>
);

// ── Single message block ──────────────────────────────────────────────────────

const MessageBlock = ({ msg, onReply }) => {
  const isOutbound = msg.direction === 'outbound';

  return (
    <Box
      bg={isOutbound ? 'rgba(157, 113, 253, 0.06)' : 'rgba(255, 255, 255, 0.035)'}
      border="1px solid"
      borderColor={isOutbound ? 'rgba(157, 113, 253, 0.16)' : 'rgba(255, 255, 255, 0.07)'}
      borderRadius="14px"
      overflow="hidden"
    >
      {/* Block header */}
      <HStack
        px={4}
        py={3}
        spacing={3}
        borderBottom="1px solid"
        borderColor={isOutbound ? 'rgba(157, 113, 253, 0.1)' : 'rgba(255, 255, 255, 0.05)'}
        align="flex-start"
      >
        <Avatar email={msg.from_email} isOutbound={isOutbound} />

        <Box flex={1} minW={0}>
          {/* From + timestamp */}
          <HStack justify="space-between" align="baseline" spacing={2} mb={0.5}>
            <Text
              color={isOutbound ? 'rgba(157, 113, 253, 0.85)' : 'white'}
              fontSize="13px"
              fontWeight="600"
              noOfLines={1}
            >
              {msg.from_email}
            </Text>
            <Text color="rgba(255,255,255,0.28)" fontSize="11px" flexShrink={0}>
              {formatFullTime(msg.received_at)}
            </Text>
          </HStack>

          {/* To line */}
          <Text color="rgba(255,255,255,0.35)" fontSize="11px" noOfLines={1}>
            To: {msg.to_email}
          </Text>
        </Box>
      </HStack>

      {/* Body — quoted tail stripped at display time; full text lives in MongoDB */}
      <Box px={4} pt={3} pb={!isOutbound ? 2 : 4}>
        <Text
          color={isOutbound ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.82)'}
          fontSize="13px"
          lineHeight="1.75"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {stripQuotedTail(msg.body_text) || '(no content)'}
        </Text>
      </Box>

      {/* Reply action — inbound messages only */}
      {!isOutbound && (
        <Box px={4} pb={3}>
          <button
            onClick={onReply}
            style={{
              display:        'inline-flex',
              alignItems:     'center',
              gap:            '5px',
              padding:        '4px 10px',
              borderRadius:   '6px',
              border:         '1px solid rgba(255,255,255,0.1)',
              background:     'rgba(255,255,255,0.04)',
              color:          'rgba(255,255,255,0.45)',
              fontSize:       '12px',
              fontWeight:     '500',
              cursor:         'pointer',
              letterSpacing:  '0.01em',
              transition:     'background 0.15s, color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background    = 'rgba(255,255,255,0.08)';
              e.currentTarget.style.color         = 'rgba(255,255,255,0.75)';
              e.currentTarget.style.borderColor   = 'rgba(255,255,255,0.18)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background    = 'rgba(255,255,255,0.04)';
              e.currentTarget.style.color         = 'rgba(255,255,255,0.45)';
              e.currentTarget.style.borderColor   = 'rgba(255,255,255,0.1)';
            }}
          >
            <LuReply size={12} />
            Reply
          </button>
        </Box>
      )}
    </Box>
  );
};

// ── Inline compose box ────────────────────────────────────────────────────────

const ComposeBox = ({ toEmail, body, onBodyChange, onSend, onDiscard, isSending }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, [body]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const canSend = body.trim().length > 0 && !isSending;

  return (
    <Box
      bg="rgba(255,255,255,0.035)"
      border="1px solid rgba(255,255,255,0.09)"
      borderRadius="14px"
      overflow="hidden"
    >
      {/* To row */}
      <HStack
        px={4}
        py="10px"
        spacing={2}
        borderBottom="1px solid rgba(255,255,255,0.05)"
      >
        <Text
          fontSize="12px"
          fontWeight="500"
          color="rgba(255,255,255,0.3)"
          flexShrink={0}
        >
          To
        </Text>
        <Box
          px={2}
          py="3px"
          bg="rgba(99,179,237,0.08)"
          border="1px solid rgba(99,179,237,0.18)"
          borderRadius="6px"
        >
          <Text fontSize="12px" color="rgba(99,179,237,0.8)" lineHeight={1.4}>
            {toEmail}
          </Text>
        </Box>
      </HStack>

      {/* Textarea */}
      <Box px={4} pt={3} pb={2}>
        <textarea
          ref={textareaRef}
          value={body}
          onChange={e => onBodyChange(e.target.value)}
          placeholder="Write a reply…"
          rows={3}
          style={{
            width:       '100%',
            background:  'transparent',
            border:      'none',
            outline:     'none',
            color:       'rgba(255,255,255,0.82)',
            fontSize:    '13px',
            lineHeight:  '1.75',
            resize:      'none',
            fontFamily:  'inherit',
            minHeight:   '72px',
            maxHeight:   '200px',
            overflowY:   'auto',
          }}
        />
      </Box>

      {/* Action bar */}
      <HStack px={4} pb={3} justify="space-between" align="center">
        {/* Send */}
        <button
          onClick={onSend}
          disabled={!canSend}
          style={{
            display:       'inline-flex',
            alignItems:    'center',
            gap:           '6px',
            padding:       '6px 14px',
            borderRadius:  '8px',
            border:        'none',
            background:    canSend ? 'rgba(157,113,253,0.85)' : 'rgba(157,113,253,0.25)',
            color:         canSend ? 'white' : 'rgba(255,255,255,0.3)',
            fontSize:      '13px',
            fontWeight:    '500',
            cursor:        canSend ? 'pointer' : 'not-allowed',
            transition:    'background 0.15s',
          }}
          onMouseEnter={e => {
            if (canSend) e.currentTarget.style.background = 'rgba(157,113,253,1)';
          }}
          onMouseLeave={e => {
            if (canSend) e.currentTarget.style.background = 'rgba(157,113,253,0.85)';
          }}
        >
          {isSending ? (
            <Spinner size="xs" thickness="1.5px" color="white" />
          ) : (
            <LuSend size={13} />
          )}
          {isSending ? 'Sending…' : 'Send'}
        </button>

        {/* Discard */}
        <button
          onClick={onDiscard}
          title="Discard draft"
          style={{
            display:      'inline-flex',
            alignItems:   'center',
            justifyContent: 'center',
            width:        '30px',
            height:       '30px',
            borderRadius: '7px',
            border:       '1px solid rgba(255,255,255,0.08)',
            background:   'transparent',
            color:        'rgba(255,255,255,0.3)',
            cursor:       'pointer',
            transition:   'background 0.15s, color 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = 'rgba(239,68,68,0.12)';
            e.currentTarget.style.color      = 'rgba(239,68,68,0.7)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color      = 'rgba(255,255,255,0.3)';
          }}
        >
          <LuTrash2 size={13} />
        </button>
      </HStack>
    </Box>
  );
};

// ── ThreadView ────────────────────────────────────────────────────────────────

const ThreadView = ({ thread, campaignId }) => {
  const [messages,     setMessages]     = useState([]);
  const [isLoading,    setIsLoading]    = useState(false);
  const [composeOpen,  setComposeOpen]  = useState(false);
  const [replyBody,    setReplyBody]    = useState('');
  const [isSending,    setIsSending]    = useState(false);

  const fetchThread = useCallback(async () => {
    if (!thread || !campaignId) return;
    setIsLoading(true);
    setMessages([]);
    try {
      const { data } = await api.get(
        `/api/inbox/${campaignId}/threads/${thread.thread_id}`
      );
      setMessages(data.messages ?? []);

      if (!thread.is_read) {
        api.patch(`/api/inbox/replies/${thread.message_id}/read`, { is_read: true })
          .catch(() => {});
      }
    } catch {
      setMessages([]);
    } finally {
      setIsLoading(false);
    }
  }, [thread, campaignId]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!thread || !campaignId) return;
      setIsLoading(true);
      setMessages([]);
      setComposeOpen(false);
      setReplyBody('');
      try {
        const { data } = await api.get(
          `/api/inbox/${campaignId}/threads/${thread.thread_id}`
        );
        if (cancelled) return;
        setMessages(data.messages ?? []);

        if (!thread.is_read) {
          api.patch(`/api/inbox/replies/${thread.message_id}/read`, { is_read: true })
            .catch(() => {});
        }
      } catch {
        if (!cancelled) setMessages([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [thread, campaignId]);

  const prospectEmail = messages.find(m => m.direction === 'inbound')?.from_email ?? '';

  const handleOpenCompose = useCallback(() => {
    setComposeOpen(true);
  }, []);

  const handleDiscard = useCallback(() => {
    setComposeOpen(false);
    setReplyBody('');
  }, []);

  const handleSend = useCallback(async () => {
    if (!replyBody.trim() || isSending) return;
    setIsSending(true);
    try {
      await api.post(
        `/api/inbox/${campaignId}/threads/${thread.thread_id}/reply`,
        { body: replyBody }
      );
      setComposeOpen(false);
      setReplyBody('');
      await fetchThread();
    } catch (err) {
      console.error('Reply send failed:', err);
    } finally {
      setIsSending(false);
    }
  }, [replyBody, isSending, campaignId, thread, fetchThread]);

  // ── Loading ──────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Box h="100%" w="100%" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="md" color="rgba(157,113,253,0.6)" thickness="2px" />
      </Box>
    );
  }

  // ── Empty ────────────────────────────────────────────────────────────────

  if (messages.length === 0) {
    return (
      <Box h="100%" w="100%" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={3} align="center">
          <Box color="rgba(157,113,253,0.3)">
            <LuMailOpen size={32} />
          </Box>
          <Text color="rgba(255,255,255,0.2)" fontSize="sm">
            No messages in this thread
          </Text>
        </VStack>
      </Box>
    );
  }

  // ── Thread ───────────────────────────────────────────────────────────────

  return (
    <Box h="100%" w="100%" display="flex" flexDirection="column" overflow="hidden">

      {/* Subject header */}
      <Box
        px={5}
        pt={5}
        pb={3}
        borderBottom="1px solid rgba(255,255,255,0.06)"
        flexShrink={0}
      >
        <Text
          color="white"
          fontSize="16px"
          fontWeight="600"
          letterSpacing="-0.01em"
          noOfLines={2}
        >
          {thread.subject || '(no subject)'}
        </Text>
        <Text color="rgba(255,255,255,0.3)" fontSize="11px" mt={1}>
          {messages.length} message{messages.length !== 1 ? 's' : ''}
        </Text>
      </Box>

      {/* Compose box — sits above the thread messages, exactly like Outlook */}
      {composeOpen && (
        <Box px={5} pt={4} flexShrink={0}>
          <ComposeBox
            toEmail={prospectEmail}
            body={replyBody}
            onBodyChange={setReplyBody}
            onSend={handleSend}
            onDiscard={handleDiscard}
            isSending={isSending}
          />
        </Box>
      )}

      {/* Message blocks — scrollable */}
      <Box
        flex={1}
        overflowY="auto"
        px={5}
        py={4}
        sx={{
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            bg: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
          },
        }}
      >
        <VStack spacing={3} align="stretch">
          {[...messages].reverse().map((msg) => (
            <MessageBlock
              key={msg.message_id ?? msg._id}
              msg={msg}
              onReply={handleOpenCompose}
            />
          ))}
        </VStack>
      </Box>

    </Box>
  );
};

export default ThreadView;
